'use client';

import { clearSurveyProgressAction } from '@/actions/user';
import { clearSurveySession, loadSurveyProgress } from '@/util/survey-storage';
import { getTTL } from '@/util/ttl';
import { isGuestUser } from '@/util/util';
import { useSearchParams } from 'next/navigation';
import { useCallback, useEffect, useRef, useState } from 'react';
import CategoryPage from './category-page';
import { getStoredSessionGroupId, storeSessionGroupId } from './helper';

interface CategoryPageWrapperProps {
    slug: string;
    lang?: string; // Language code (en, th, etc.)
    categoriesData: Array<any>;
    surveyForm: any;
    userId?: string; // R-commerce user ID when logged in
    userEmail?: string | null; // User email from session
    user?: any; // User object with level (optional)
}

const SKIP_ASK_LOGIN_STORAGE_KEY = (slug: string) =>
    `survey_skip_ask_login_${slug}`;
const FROM_SIGNUP_MARKER_KEY = (slug: string) => `survey_from_signup_${slug}`;
const FROM_SURVEY_URL_PARAM = 'fromSurvey';

/**
 * Wrapper component that handles the resume decision flow
 * Passes showResumeContent flag to CategoryPage instead of rendering separate page
 */
export default function CategoryPageWrapper(props: CategoryPageWrapperProps) {
    const searchParams = useSearchParams();
    const actionParam = searchParams.get('action');
    const initialIsViewResultMode = actionParam === 'view-result';
    const lastInitKeyRef = useRef<string | null>(null);
    const prevUserIdRef = useRef<string | undefined>(props.userId);
    const [showResumeContent, setShowResumeContent] = useState(false);
    const [showAskLogin, setShowAskLogin] = useState(false);
    const [restartGroupId, setRestartGroupId] = useState<string | null>(null);
    const [isCompletedSurvey, setIsCompletedSurvey] = useState(false);
    // True until the FIRST saved-progress lookup settles; acting before it could start a fresh
    // attempt under a resume dialog still on its way. Re-inits (guest created, restart) keep the page.
    const [isInitializing, setIsInitializing] = useState(true);

    // New state to track if we're in view result mode (action=view-result)
    const [isViewResultMode, setIsViewResultMode] = useState(
        initialIsViewResultMode,
    );

    // A "real" member: has both userId and a non-guest email. Guests have @dummy.com emails.
    const isLoggedInMember =
        !!props.userId && !!props.userEmail && !isGuestUser(props.userEmail);

    const initializeSurvey = useCallback(async () => {
        const currentActionParam = searchParams.get('action');
        const viewResultMode = currentActionParam === 'view-result';
        setIsViewResultMode(viewResultMode);

        // A real logged-in member never needs the askLogin prompt.
        // Clear any stale dismiss flag they might have set while anonymous
        // (e.g. they dismissed, then registered, then later logged out).
        // This ensures re-prompting happens correctly after logout.
        if (isLoggedInMember && typeof window !== 'undefined') {
            localStorage.removeItem(SKIP_ASK_LOGIN_STORAGE_KEY(props.slug));
        }

        if (viewResultMode) {
            let storedId = getStoredSessionGroupId(props.slug);
            if (!storedId) {
                storedId = storeSessionGroupId(props.slug);
            }
            setRestartGroupId(storedId);
            setShowResumeContent(false);
            setShowAskLogin(false);
            setIsCompletedSurvey(true);
            return;
        }

        // Detect "comeback from signup": URL param or sessionStorage marker.
        // When found, skip every dialog and go straight to the survey — preventing
        // stale resume prompts (e.g. guest progress still in DB) from interrupting
        // a freshly-authenticated member.
        let cameBackFromSignup = false;
        if (typeof window !== 'undefined') {
            const urlMarker = searchParams.get(FROM_SURVEY_URL_PARAM) === '1';
            const sessionMarker =
                sessionStorage.getItem(FROM_SIGNUP_MARKER_KEY(props.slug)) ===
                '1';
            cameBackFromSignup = urlMarker || sessionMarker;

            if (cameBackFromSignup) {
                sessionStorage.removeItem(FROM_SIGNUP_MARKER_KEY(props.slug));
                if (urlMarker) {
                    const url = new URL(window.location.href);
                    url.searchParams.delete(FROM_SURVEY_URL_PARAM);
                    window.history.replaceState({}, '', url.toString());
                }
            }
        }

        // DF-20: only treat "came back from signup" as a reason to skip the prompts
        // when signup ACTUALLY succeeded (the user is now a logged-in member). If the
        // user opened signin and pressed Back without authenticating, they are still a
        // guest/anonymous — we must NOT skip. We reset to the "no prior action" state
        // and fall through to the normal flow so the askLogin prompt is shown again,
        // exactly as if they had just entered the survey.
        if (cameBackFromSignup && isLoggedInMember) {
            let storedId = getStoredSessionGroupId(props.slug);
            if (!storedId) {
                storedId = storeSessionGroupId(props.slug);
            }
            setRestartGroupId(storedId);
            setShowResumeContent(false);
            setShowAskLogin(false);
            setIsCompletedSurvey(false);
            return;
        }

        // DF-20: returned from an ABANDONED signup (back button, still a guest).
        // Reset to the "no prior action" state by dropping any askLogin dismiss flag,
        // so the normal flow below re-prompts the guest just like a fresh entry.
        if (
            cameBackFromSignup &&
            !isLoggedInMember &&
            typeof window !== 'undefined'
        ) {
            localStorage.removeItem(SKIP_ASK_LOGIN_STORAGE_KEY(props.slug));
        }

        let savedProgress = null as Awaited<
            ReturnType<typeof loadSurveyProgress>
        >;

        // Only check for saved progress if userId is available
        if (props.userId) {
            try {
                const ttlMs = await getTTL('NEXT_PUBLIC_SURVEY_TTL_SECONDS');
                savedProgress = await loadSurveyProgress(
                    props.slug,
                    props.userId,
                    { ttlMs },
                );
            } catch {
                savedProgress = await loadSurveyProgress(
                    props.slug,
                    props.userId,
                );
            }
        }

        let existingResponseRestartGroupId: string | null = null;
        if (
            !savedProgress?.restartGroupId &&
            props.userId &&
            props.surveyForm?.id
        ) {
            try {
                const { getActiveSurveyRestartGroupId } = await import(
                    '@/actions/survey'
                );
                const result = await getActiveSurveyRestartGroupId(
                    props.userId,
                    props.surveyForm.id,
                );
                if (result.success && result.restartGroupId) {
                    existingResponseRestartGroupId = result.restartGroupId;
                }
            } catch (error) {
                console.error(
                    '❌ Failed to load active restartGroupId:',
                    error,
                );
            }
        }

        const persistedRestartGroupId =
            savedProgress?.restartGroupId ||
            existingResponseRestartGroupId ||
            null;
        let storedId =
            persistedRestartGroupId || getStoredSessionGroupId(props.slug);
        if (!storedId) {
            storedId = storeSessionGroupId(props.slug);
        } else if (
            persistedRestartGroupId &&
            persistedRestartGroupId !== getStoredSessionGroupId(props.slug)
        ) {
            storeSessionGroupId(props.slug, persistedRestartGroupId);
        }
        setRestartGroupId(storedId);

        // Compute whether the askLogin prompt should be shown for this (guest/anonymous)
        // identity. The flag value encodes the identity that dismissed the prompt:
        //   'anonymous' (or legacy '1') → dismissed when no account existed
        //   '<userId>'                  → dismissed while logged in as that userId (guest)
        // If the flag doesn't match the current identity, the session changed
        // (e.g., the user logged out) so we clear it and re-prompt.
        let hasDismissedAskLogin = false;
        if (typeof window !== 'undefined') {
            const flagValue = localStorage.getItem(
                SKIP_ASK_LOGIN_STORAGE_KEY(props.slug),
            );
            if (flagValue !== null) {
                const isAnonymousFlag =
                    flagValue === '1' || flagValue === 'anonymous';
                const isCurrentlyAnonymous = !props.userId;
                if (isAnonymousFlag && isCurrentlyAnonymous) {
                    hasDismissedAskLogin = true;
                } else if (!isAnonymousFlag && props.userId === flagValue) {
                    hasDismissedAskLogin = true;
                } else {
                    // Identity mismatch → this flag belongs to a previous session; clear it
                    localStorage.removeItem(
                        SKIP_ASK_LOGIN_STORAGE_KEY(props.slug),
                    );
                }
            }
        }
        const shouldAskLogin = !isLoggedInMember && !hasDismissedAskLogin;

        // DF-21: For a guest who hasn't decided yet, the askLogin prompt must come
        // FIRST — before the resume/edit dialog — regardless of whether the survey is
        // completed or in progress. We still set the correct underlying resume state
        // (isCompletedSurvey / showResumeContent) so that once the guest picks
        // "continue as guest" (which clears showAskLogin), the resume/edit dialog they
        // would normally see is revealed underneath, with no change to those flows.
        if (
            savedProgress &&
            savedProgress.isCompleted &&
            currentActionParam !== 'edit'
        ) {
            // Survey was completed - show resume dialog to let them choose
            setIsCompletedSurvey(true);
            setShowResumeContent(true);
            setShowAskLogin(shouldAskLogin);
        } else if (savedProgress && !currentActionParam) {
            // Survey in progress - show resume dialog
            setIsCompletedSurvey(false);
            setShowResumeContent(true);
            setShowAskLogin(shouldAskLogin);
        } else {
            // New survey or action param present (restart/edit) — fall-through case.
            // Show the askLogin prompt to guests/anonymous users who haven't already
            // dismissed it. Real logged-in members skip this entirely.
            setIsCompletedSurvey(false);
            setShowResumeContent(false);
            setShowAskLogin(shouldAskLogin);
        }
    }, [
        props.slug,
        props.userId,
        props.userEmail,
        searchParams,
        isLoggedInMember,
    ]);

    // Keep the dismiss flag in sync with the current identity.
    //
    // Two transitions matter:
    //  - anonymous → guestId (guest account just created):
    //      Upgrade flag from 'anonymous'/'1' to the actual guestId so that
    //      initializeSurvey can detect a logout (guestId flag vs. anonymous session).
    //  - guestId → undefined (logout while still on the page):
    //      Clear the flag immediately so initializeSurvey re-prompts.
    useEffect(() => {
        const prevUserId = prevUserIdRef.current;
        prevUserIdRef.current = props.userId;

        if (typeof window === 'undefined') return;

        if (!prevUserId && props.userId) {
            // Guest account was just created from an anonymous state.
            // Upgrade the flag so a future logout triggers re-prompting.
            const flag = localStorage.getItem(
                SKIP_ASK_LOGIN_STORAGE_KEY(props.slug),
            );
            if (flag === 'anonymous' || flag === '1') {
                localStorage.setItem(
                    SKIP_ASK_LOGIN_STORAGE_KEY(props.slug),
                    props.userId,
                );
            }
        } else if (prevUserId && !props.userId) {
            // Logout (same-page transition): clear the flag and force re-init.
            localStorage.removeItem(SKIP_ASK_LOGIN_STORAGE_KEY(props.slug));
            lastInitKeyRef.current = null;
        }
    }, [props.userId, props.slug]);

    const runInitialization = useCallback(async () => {
        try {
            await initializeSurvey();
        } finally {
            setIsInitializing(false);
        }
    }, [initializeSurvey]);

    const makeInitKey = useCallback(
        (actionParam: string, fromSurveyParam: string) =>
            `${props.slug}:${
                props.userId || 'anonymous'
            }:${actionParam}:${fromSurveyParam}`,
        [props.slug, props.userId],
    );

    useEffect(() => {
        const initKey = makeInitKey(
            searchParams.get('action') || '',
            searchParams.get(FROM_SURVEY_URL_PARAM) || '',
        );
        if (lastInitKeyRef.current === initKey) return;
        lastInitKeyRef.current = initKey;
        void runInitialization();
    }, [runInitialization, makeInitKey, searchParams]);

    const handleRestart = async () => {
        // Clear survey progress metadata and mark database responses as restarted
        if (props.userId && props.surveyForm?.id) {
            const result = await clearSurveyProgressAction(
                props.slug,
                props.userId,
                props.surveyForm?.id,
            );

            if (!result.success) {
                console.error(
                    '❌ [handleRestart] Failed to clear survey progress:',
                    result.error,
                );
            }
        }

        clearSurveySession(props.slug);

        // Drop the askLogin dismiss flag so the prompt reappears for guests on the next init —
        // per requirement: "กลับมาทำต่อ/แก้ไขแล้วกด เริ่มใหม่" should re-ask the login decision.
        if (typeof window !== 'undefined') {
            localStorage.removeItem(SKIP_ASK_LOGIN_STORAGE_KEY(props.slug));
        }

        const newRestartGroupId = storeSessionGroupId(props.slug);
        setRestartGroupId(newRestartGroupId);

        setIsViewResultMode(false); // Reset view result mode on restart

        // Clear query parameters to reset to first category
        if (typeof window !== 'undefined') {
            const url = new URL(window.location.href);
            url.search = ''; // Remove all query parameters
            window.history.replaceState({}, '', url.toString());
        }

        // Re-initialize explicitly against the just-cleared state. Awaited
        // (not fire-and-forget) so the caller — handleRestartFromResume in
        // CategoryPage — can rely on this having fully re-checked for saved
        // progress (finding none, since we just cleared it) before revealing
        // the survey UI.
        //
        // The ref is SET to the post-restart key (action/from params were
        // just cleared above), NOT nulled: a null ref arms the init effect
        // to re-run initializeSurvey on the NEXT searchParams change — which
        // is typically the ?category= navigation into category 2 — and by
        // then the new attempt's own answers have auto-saved, so that re-run
        // finds "saved progress" and re-opens the resume dialog mid-attempt.
        lastInitKeyRef.current = makeInitKey('', '');
        await initializeSurvey();
    };

    const handleSignUpFromAskLogin = () => {
        if (typeof window === 'undefined') return;
        const lang = props.lang || 'th';
        const surveyUrl = `/${lang}/survey-form/${props.slug}?${FROM_SURVEY_URL_PARAM}=1`;
        const signinUrl = `/${lang}/signin?callbackUrl=${encodeURIComponent(
            surveyUrl,
        )}`;
        // Belt-and-suspenders: also persist a sessionStorage marker in case the
        // callbackUrl is altered by the auth flow.
        sessionStorage.setItem(FROM_SIGNUP_MARKER_KEY(props.slug), '1');
        // Use full navigation so the session/cookies refresh after signin.
        window.location.href = signinUrl;
    };

    const handleContinueAsGuest = () => {
        if (typeof window !== 'undefined') {
            // Store the current identity so initializeSurvey can tell if the
            // session has changed (e.g., after logout) and re-prompt accordingly.
            localStorage.setItem(
                SKIP_ASK_LOGIN_STORAGE_KEY(props.slug),
                props.userId || 'anonymous',
            );
        }
        setShowAskLogin(false);
    };

    // Pass showResumeContent flag, userId, userEmail, and callback to CategoryPage
    return (
        <CategoryPage
            {...props}
            showResumeContent={showResumeContent || showAskLogin}
            isCompletedSurvey={isCompletedSurvey}
            userId={props.userId}
            userEmail={props.userEmail}
            user={props.user}
            restartGroupId={restartGroupId || undefined}
            isInitializing={isInitializing}
            onResumeDecisionMade={() => setShowResumeContent(false)}
            onRestartSurvey={handleRestart}
            isViewResultMode={isViewResultMode}
            resumeDecisionMode={showAskLogin ? 'askLogin' : 'resume'}
            onSignUpFromAskLogin={handleSignUpFromAskLogin}
            onContinueAsGuest={handleContinueAsGuest}
        />
    );
}
